/**
 * UserManagementPanel — search any member by username/email and run
 * audited admin actions: set tier, suspend/resume/cancel subscription,
 * mark test, block, soft-delete + restore, hard-delete.
 *
 * Destructive actions (block, suspend, cancel, soft-delete, hard-delete,
 * tier downgrade) require the founder password. The founder profile
 * itself is hard-protected server-side.
 */
import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useFounderAdmin, type AdminKind } from "@/lib/founder/useFounderAdmin";
import { toast } from "@/hooks/use-toast";

interface InfluencerRedemption {
  redeemed_at: string;
  code: string | null;
  label: string | null;
  granted_tier: string | null;
  app: string | null;
  notion_influencer_name: string | null;
  notion_handle: string | null;
}

interface ProfileLite {
  id: string;
  username: string | null;
  display_name: string | null;
  tier: string;
  is_test_account: boolean;
  blocked_at: string | null;
  blocked_reason: string | null;
  deleted_at: string | null;
  stripe_subscription_id: string | null;
  influencer?: InfluencerRedemption | null;
}

const TIERS = ["tier_signal","tier_pulse","tier_surge","tier_spectrum","tier_attuned"];

interface PendingAction {
  kind: AdminKind;
  target: string;
  label: string;
  payload?: Record<string, unknown>;
  reason?: string;
  requireDeleteConfirm?: boolean;
}

const PAGE_SIZE = 25;

export const UserManagementPanel = ({ initialInfluencerOnly = false }: { initialInfluencerOnly?: boolean } = {}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ProfileLite[]>([]);
  const [searching, setSearching] = useState(false);
  const [influencerOnly, setInfluencerOnly] = useState(initialInfluencerOnly);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { run, busy } = useFounderAdmin();

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const runSearch = async (targetPage: number) => {
    setSearching(true);
    const from = targetPage * PAGE_SIZE;
    const to = from + PAGE_SIZE; // fetch one extra to detect hasMore
    const trimmed = q.trim();

    if (influencerOnly && !trimmed) {
      // Fast path: drive pagination from redemptions (newest first), then hydrate profiles by id.
      const { data: redemptions } = await (supabase as any)
        .from("influencer_code_redemptions")
        .select("user_id, redeemed_at, influencer_access_codes(code, label, granted_tier, app, notion_influencer_name, notion_handle)")
        .order("redeemed_at", { ascending: false })
        .range(from, to);
      const rows = (redemptions ?? []) as Array<{
        user_id: string;
        redeemed_at: string;
        influencer_access_codes: InfluencerRedemption | null;
      }>;
      const more = rows.length > PAGE_SIZE;
      const pageRows = rows.slice(0, PAGE_SIZE);
      const redemptionByUser: Record<string, InfluencerRedemption> = {};
      const ids: string[] = [];
      for (const r of pageRows) {
        if (!redemptionByUser[r.user_id]) {
          ids.push(r.user_id);
          redemptionByUser[r.user_id] = {
            redeemed_at: r.redeemed_at,
            code: r.influencer_access_codes?.code ?? null,
            label: r.influencer_access_codes?.label ?? null,
            granted_tier: r.influencer_access_codes?.granted_tier ?? null,
            app: r.influencer_access_codes?.app ?? null,
            notion_influencer_name: r.influencer_access_codes?.notion_influencer_name ?? null,
            notion_handle: r.influencer_access_codes?.notion_handle ?? null,
          };
        }
      }
      if (ids.length === 0) {
        setResults([]);
        setHasMore(false);
        setSearching(false);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("id, username, display_name, tier, is_test_account, blocked_at, blocked_reason, deleted_at, stripe_subscription_id")
        .in("id", ids);
      const byId = new Map(((data as ProfileLite[] | null) ?? []).map((p) => [p.id, p]));
      const ordered: ProfileLite[] = ids
        .map((id) => byId.get(id))
        .filter((p): p is ProfileLite => Boolean(p))
        .map((p) => ({ ...p, influencer: redemptionByUser[p.id] ?? null }));
      setResults(ordered);
      setHasMore(more);
      setSearching(false);
      return;
    }

    // Search path: query profiles first, then enrich with redemption metadata only when influencerOnly is on.
    let query = supabase
      .from("profiles")
      .select("id, username, display_name, tier, is_test_account, blocked_at, blocked_reason, deleted_at, stripe_subscription_id");

    if (trimmed) {
      query = query.or(`username.ilike.%${trimmed}%,display_name.ilike.%${trimmed}%,id.eq.${trimmed}`);
    }
    const { data } = await query.range(from, to);
    let profiles = (data as ProfileLite[] | null) ?? [];
    const more = profiles.length > PAGE_SIZE;
    profiles = profiles.slice(0, PAGE_SIZE);

    if (influencerOnly && profiles.length > 0) {
      const ids = profiles.map((p) => p.id);
      const { data: redemptions } = await (supabase as any)
        .from("influencer_code_redemptions")
        .select("user_id, redeemed_at, influencer_access_codes(code, label, granted_tier, app, notion_influencer_name, notion_handle)")
        .in("user_id", ids)
        .order("redeemed_at", { ascending: false });
      const redemptionByUser: Record<string, InfluencerRedemption> = {};
      for (const r of (redemptions ?? []) as Array<{
        user_id: string;
        redeemed_at: string;
        influencer_access_codes: InfluencerRedemption | null;
      }>) {
        if (!redemptionByUser[r.user_id]) {
          redemptionByUser[r.user_id] = {
            redeemed_at: r.redeemed_at,
            code: r.influencer_access_codes?.code ?? null,
            label: r.influencer_access_codes?.label ?? null,
            granted_tier: r.influencer_access_codes?.granted_tier ?? null,
            app: r.influencer_access_codes?.app ?? null,
            notion_influencer_name: r.influencer_access_codes?.notion_influencer_name ?? null,
            notion_handle: r.influencer_access_codes?.notion_handle ?? null,
          };
        }
      }
      profiles = profiles
        .filter((p) => redemptionByUser[p.id])
        .map((p) => ({ ...p, influencer: redemptionByUser[p.id] }));
    }

    setResults(profiles);
    setHasMore(more);
    setSearching(false);
  };

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(0);
    setSelected(new Set());
    await runSearch(0);
  };

  const goToPage = async (next: number) => {
    if (next < 0) return;
    setPage(next);
    setSelected(new Set());
    await runSearch(next);
  };

  const refresh = () => runSearch(page);

  // Auto-load when arriving via the dedicated "Influencers only" tab.
  const autoLoaded = useRef(false);
  useEffect(() => {
    if (initialInfluencerOnly && !autoLoaded.current) {
      autoLoaded.current = true;
      void runSearch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInfluencerOnly]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllInfluencers = () => {
    const ids = results.filter((p) => p.influencer).map((p) => p.id);
    setSelected(new Set(ids));
  };

  const clearSelection = () => setSelected(new Set());

  const runBulkAdminKind = async (kind: AdminKind, label: string) => {
    if (selected.size === 0) return;
    if (!confirm(`${label} ${selected.size} influencer account(s)?`)) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      const res = await run({ kind, target_user_id: id, reason: `bulk:${kind}` });
      if (res.ok) ok++;
      else fail++;
    }
    setBulkBusy(false);
    toast({
      title: `${label} done`,
      description: `${ok} succeeded${fail ? `, ${fail} failed` : ""}`,
      variant: fail > 0 ? "destructive" : "default",
    });
    clearSelection();
    await refresh();
  };

  const runBulkResendInvite = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Resend influencer invite email to ${selected.size} account(s)?`)) return;
    setBulkBusy(true);
    const { data, error } = await supabase.functions.invoke<{
      ok: boolean;
      sent?: string[];
      skipped?: { user_id: string; reason: string }[];
      error?: string;
    }>("founder-resend-influencer-invite", {
      body: { user_ids: Array.from(selected) },
    });
    setBulkBusy(false);
    if (error || !data?.ok) {
      toast({
        title: "Resend failed",
        description: error?.message ?? data?.error ?? "Unknown error",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Invites resent",
      description: `${data.sent?.length ?? 0} sent${
        data.skipped?.length ? `, ${data.skipped.length} skipped` : ""
      }`,
    });
    clearSelection();
  };

  const directRun = async (
    target: string,
    kind: AdminKind,
    payload?: Record<string, unknown>,
    reason?: string,
  ) => {
    const res = await run({ kind, target_user_id: target, payload, reason });
    if (res.ok) {
      toast({ title: "Done", description: kind });
      await refresh();
    } else {
      toast({ title: "Failed", description: res.error ?? "Unknown error", variant: "destructive" });
    }
  };

  const requestWithPassword = (action: PendingAction) => {
    setPending(action);
    setPassword("");
    setConfirmText("");
  };

  const submitPending = async () => {
    if (!pending) return;
    if (pending.requireDeleteConfirm && confirmText !== "DELETE") {
      toast({ title: "Type DELETE to confirm", variant: "destructive" });
      return;
    }
    if (!password) {
      toast({ title: "Password required", variant: "destructive" });
      return;
    }
    const payload = { ...(pending.payload ?? {}) };
    if (pending.requireDeleteConfirm) payload.confirm = "DELETE";
    const res = await run({
      kind: pending.kind,
      target_user_id: pending.target,
      payload,
      reason: pending.reason,
      password,
    });
    if (res.ok) {
      toast({ title: "Done", description: pending.kind });
      setPending(null);
      setPassword("");
      setConfirmText("");
      await refresh();
    } else {
      toast({ title: "Failed", description: res.error ?? "Unknown error", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <Input
          placeholder="Search by username, display name, or UUID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[220px]"
        />
        <Button
          type="button"
          variant={influencerOnly ? "default" : "outline"}
          onClick={() => setInfluencerOnly((v) => !v)}
          className={influencerOnly ? "font-bold" : "font-bold text-primary border-primary/50"}
          title="Only members who redeemed an influencer access code"
        >
          {influencerOnly ? "✓ Influencers only" : "Influencers only"}
        </Button>
        <Button type="submit" disabled={searching}>
          {searching ? "Searching…" : "Search"}
        </Button>
      </form>

      {influencerOnly && results.some((p) => p.influencer) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-2">
          <span className="text-xs font-semibold text-primary">
            {selected.size} selected
          </span>
          <Button
            type="button" size="sm" variant="ghost"
            onClick={selectAllInfluencers}
            disabled={bulkBusy}
          >
            Select all on page
          </Button>
          <Button
            type="button" size="sm" variant="ghost"
            onClick={clearSelection}
            disabled={bulkBusy || selected.size === 0}
          >
            Clear
          </Button>
          <div className="h-5 w-px bg-border mx-1" />
          <Button
            type="button" size="sm" variant="outline"
            disabled={bulkBusy || selected.size === 0}
            onClick={() => runBulkAdminKind("block_user", "Block")}
          >
            Block
          </Button>
          <Button
            type="button" size="sm" variant="outline"
            disabled={bulkBusy || selected.size === 0}
            onClick={() => runBulkAdminKind("unblock_user", "Unblock")}
          >
            Unblock
          </Button>
          <Button
            type="button" size="sm" variant="default"
            disabled={bulkBusy || selected.size === 0}
            onClick={runBulkResendInvite}
          >
            {bulkBusy ? "Working…" : "Resend invite"}
          </Button>
        </div>
      )}

      {results.length === 0 ? (
        <p className="text-sm text-muted-foreground">No results yet — search to find a member.</p>
      ) : (
        <ul className="space-y-3">
          {results.map((p) => (
            <li key={p.id}>
              <Card className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    {p.influencer && (
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-primary cursor-pointer"
                        checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        aria-label={`Select ${p.username ?? p.id}`}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {p.display_name || p.username || "(no name)"}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{p.id}</div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge variant="secondary">{p.tier.replace("tier_", "")}</Badge>
                      {p.is_test_account && <Badge variant="outline">test account</Badge>}
                      {p.blocked_at && <Badge variant="destructive">blocked</Badge>}
                      {p.deleted_at && <Badge variant="destructive">deleted</Badge>}
                      {p.stripe_subscription_id && <Badge variant="outline">subscribed</Badge>}
                      {p.influencer && <Badge className="bg-primary/15 text-primary border-primary/30">influencer</Badge>}
                    </div>
                    {p.influencer && (
                      <div className="mt-2 rounded-md border border-primary/20 bg-primary/5 p-2 text-xs space-y-0.5">
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-semibold text-primary">Influencer access</span>
                          <span className="text-muted-foreground">
                            redeemed {new Date(p.influencer.redeemed_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                          {p.influencer.code && <span>code <span className="font-mono text-foreground">{p.influencer.code}</span></span>}
                          {p.influencer.granted_tier && <span>tier {p.influencer.granted_tier.replace("tier_", "")}</span>}
                          {p.influencer.app && <span>app {p.influencer.app}</span>}
                        </div>
                        {(p.influencer.notion_influencer_name || p.influencer.notion_handle || p.influencer.label) && (
                          <div className="text-muted-foreground truncate">
                            {p.influencer.notion_influencer_name || p.influencer.label}
                            {p.influencer.notion_handle && <span className="ml-1">· {p.influencer.notion_handle}</span>}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                </div>

                {/* Tier change */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Select
                    onValueChange={(v) => {
                      const TIER_RANK: Record<string, number> = {
                        tier_signal: 0, tier_pulse: 1, tier_surge: 2, tier_spectrum: 3, tier_attuned: 4,
                      };
                      const isDowngrade = (TIER_RANK[v] ?? -1) < (TIER_RANK[p.tier] ?? -1);
                      if (isDowngrade) {
                        requestWithPassword({
                          kind: "set_tier", target: p.id,
                          label: `Downgrade to ${v.replace("tier_", "")}`,
                          payload: { tier: v },
                        });
                      } else {
                        directRun(p.id, "set_tier", { tier: v });
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Set tier…" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIERS.map((t) => (
                        <SelectItem key={t} value={t}>{t.replace("tier_", "")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => directRun(p.id, p.is_test_account ? "unmark_test_account" : "mark_test_account")}
                  >
                    {p.is_test_account ? "Unmark test" : "Mark test"}
                  </Button>

                  <Button
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => {
                      if (p.blocked_at) return directRun(p.id, "unblock_user");
                      const reason = prompt("Block reason?") ?? "";
                      if (!reason) return;
                      requestWithPassword({
                        kind: "block_user", target: p.id,
                        label: `Block ${p.username ?? p.id}`, reason,
                      });
                    }}
                  >
                    {p.blocked_at ? "Unblock" : "Block"}
                  </Button>
                </div>

                {/* Subscription controls */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    size="sm" variant="outline" disabled={busy || !p.stripe_subscription_id}
                    onClick={() => requestWithPassword({
                      kind: "suspend_subscription", target: p.id,
                      label: `Suspend subscription`,
                    })}
                  >
                    Suspend sub
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={busy || !p.stripe_subscription_id}
                    onClick={() => directRun(p.id, "resume_subscription")}
                  >
                    Resume sub
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={busy || !p.stripe_subscription_id}
                    onClick={() => requestWithPassword({
                      kind: "cancel_subscription", target: p.id,
                      label: `Cancel subscription`,
                    })}
                  >
                    Cancel sub
                  </Button>
                </div>

                {/* Delete / restore */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {p.deleted_at ? (
                    <Button
                      size="sm" variant="outline" disabled={busy}
                      className="col-span-2 sm:col-span-3"
                      onClick={() => directRun(p.id, "restore_user")}
                    >
                      Restore profile
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm" variant="secondary" disabled={busy}
                        className="col-span-2 sm:col-span-2"
                        onClick={() => requestWithPassword({
                          kind: "soft_delete_user", target: p.id,
                          label: `Soft-delete ${p.username ?? p.id}`,
                        })}
                      >
                        Soft delete
                      </Button>
                      <Button
                        size="sm" variant="destructive" disabled={busy}
                        onClick={() => requestWithPassword({
                          kind: "hard_delete_user", target: p.id,
                          label: `HARD DELETE ${p.username ?? p.id}`,
                          requireDeleteConfirm: true,
                        })}
                      >
                        Hard delete
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {(results.length > 0 || page > 0) && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={searching || page === 0}
            onClick={() => goToPage(page - 1)}
          >
            ← Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page + 1}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={searching || !hasMore}
            onClick={() => goToPage(page + 1)}
          >
            Next →
          </Button>
        </div>
      )}

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm: {pending?.label}</DialogTitle>
            <DialogDescription>
              This action is destructive and audited. Enter your founder password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pending?.requireDeleteConfirm && (
              <div className="space-y-1">
                <Label>Type <span className="font-mono">DELETE</span> to confirm</Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label>Founder password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={busy || !password || (pending?.requireDeleteConfirm && confirmText !== "DELETE")}
              onClick={submitPending}
            >
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPanel;
